'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import {
  Play,
  Pause,
  ArrowLeft,
  ArrowRight,
  Heart,
  Sparkle,
  Sun,
  Moon,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { VoiceOrb, useAudioElementLevel, type PresetName } from '@/components/ui/voice-orb';
import { TypographyH2, TypographyLead } from '@/components/ui/typography';
import { SectionKicker } from '@/components/brand/section-kicker';
import { BrandSparkle } from '@/components/brand/ornaments';
import { Button } from '@/components/ui/button';
import { Reveal } from '@/components/motion/reveal';
import { SectionLink } from '@/components/brand/section-link';
import { cn } from '@/lib/utils';

/**
 * "Voices" — a layered deck of standing arch cards (full-round top, soft
 * base): the active voice front and centre, one pair behind it, one pair
 * further back, each row rising, shrinking and fading toward the page.
 * Next/back (or tapping a rear card) reshuffles the deck. Every card holds
 * its orb, a small signature glyph, name and tagline; the palm-green play
 * control straddles the orb's bottom edge — half in, half out. Audio is
 * ElevenLabs TTS, one voice per persona per locale
 * (/personas/<id>-<en|ar>.mp3) — regenerate via the API if personas change.
 */

const PERSONAS: { id: string; preset: PresetName; icon: LucideIcon }[] = [
  { id: 'warm', preset: 'peach', icon: Heart },
  { id: 'pro', preset: 'forest', icon: Sparkle },
  { id: 'playful', preset: 'gold', icon: Sun },
  { id: 'calm', preset: 'aurora', icon: Moon },
  { id: 'bold', preset: 'socialmedia', icon: Zap },
];
// Only the FRONT card mounts a live WebGL orb — five contexts plus the Stack
// gallery ran into the browser's per-page context cap, which is why orbs
// sometimes stayed blank. Every card keeps a static poster of its orb
// (/personas/orb-<preset>.webp) underneath, so even a refused context shows
// the artwork instead of a hole.

export function Personas() {
  const t = useTranslations('personas');
  const items = t.raw('items') as { name: string; tagline: string }[];
  const locale = useLocale();
  const dir = locale === 'ar' ? -1 : 1;
  const n = PERSONAS.length;

  const audioRef = useRef<HTMLAudioElement>(null);
  const level = useAudioElementLevel(audioRef as RefObject<HTMLMediaElement>);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);

  // These are short UI previews, not media content — claim the hardware
  // play/pause/stop keys with no-ops so the OS media keys don't drive them.
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms) return;
    const noop = () => undefined;
    const actions = ['play', 'pause', 'stop'] as MediaSessionAction[];
    for (const action of actions) {
      try {
        ms.setActionHandler(action, noop);
      } catch {
        /* action unsupported in this browser */
      }
    }
    return () => {
      for (const action of actions) {
        try {
          ms.setActionHandler(action, null);
        } catch {
          /* action unsupported in this browser */
        }
      }
    };
  }, []);

  const play = (i: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.src = `/personas/${PERSONAS[i]!.id}-${locale === 'ar' ? 'ar' : 'en'}.mp3`;
    a.currentTime = 0;
    a.play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false));
  };

  const goto = (i: number) => {
    const next = (i + n) % n;
    setActive(next);
    play(next);
  };

  const onCardClick = (i: number) => {
    if (i !== active) return goto(i);
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      setPlaying(false);
    } else {
      play(i);
    }
  };

  return (
    <section id="voices" className="section relative overflow-hidden bg-background">
      {/* Courtyard scenes — palm, arch and pottery anchor each end of the
          section like a proscenium; their ornamental friezes sit on the same
          baseline. Text-free artwork, so it mirrors with the layout in RTL. */}
      <Image
        src="/voices-scene-start.webp"
        alt=""
        aria-hidden
        width={749}
        height={898}
        className="pointer-events-none absolute bottom-0 start-0 hidden h-[85%] w-auto select-none rtl:-scale-x-100 lg:block"
      />
      <Image
        src="/voices-scene-end.webp"
        alt=""
        aria-hidden
        width={780}
        height={898}
        className="pointer-events-none absolute bottom-0 end-0 hidden h-[85%] w-auto select-none rtl:-scale-x-100 lg:block"
      />

      <div className="container-brand relative">
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionKicker>{t('kicker')}</SectionKicker>
          <TypographyH2 className="mt-5 border-b-0 pb-0 font-display text-foreground">
            {t.rich('heading', { hl: (c) => <span className="text-brand-gold">{c}</span> })}
          </TypographyH2>
          <TypographyLead className="mx-auto mt-4 max-w-xl">{t('lede')}</TypographyLead>
        </Reveal>

        {/* Deck stage — all five cards, stepped back like a skyline. */}
        <div className="relative mx-auto mt-8 h-[360px] max-w-[900px]">
          <div className="absolute inset-0 flex items-end justify-center">
            {PERSONAS.map((p, i) => {
              // Wrapped slot: -2..2 around the active card.
              let o = (i - active + n) % n;
              if (o > n / 2) o -= n;
              const row = Math.abs(o);
              const isFront = o === 0;
              const isPlaying = isFront && playing;
              const Glyph = p.icon;
              return (
                <div
                  key={p.id}
                  role="button"
                  onClick={() => onCardClick(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCardClick(i);
                    }
                  }}
                  aria-label={isFront ? (isPlaying ? t('pause') : t('play')) : items[i]?.name}
                  tabIndex={row === 2 ? -1 : 0}
                  className="group absolute bottom-0 w-[220px] cursor-pointer outline-none transition-[transform,opacity] duration-700 ease-brand motion-reduce:transition-none"
                  style={{
                    // Tight steps — neighbours tuck in behind the front card
                    // (the far pair pulls in even tighter); the arrows straddle
                    // the front card's borders.
                    transform: `translateX(${Math.sign(o) * (row === 2 ? 330 : 190) * dir}px) translateY(${row * -24}px) scale(${1 - row * 0.14})`,
                    opacity: row === 0 ? 1 : row === 1 ? 0.75 : 0.55,
                    zIndex: 10 - row,
                    transformOrigin: 'bottom center',
                  }}
                >
                  <span
                    className={cn(
                      'flex flex-col items-center rounded-t-full rounded-b-xl border bg-card px-5 pb-8 pt-6 text-center transition-brand',
                      // The active card wears the jewelry: gold border + gold
                      // bezel around its orb (the section's one gold moment
                      // beyond kicker/headline).
                      isFront
                        ? 'border-brand-gold/45 shadow-card'
                        : 'border-border group-hover:border-primary/25',
                    )}
                  >
                    <span
                      className={cn(
                        'relative rounded-full border p-1.5 transition-brand',
                        isFront ? 'border-brand-gold/45' : 'border-transparent',
                      )}
                    >
                      <span className="relative block size-32">
                        {/* unoptimized: these are 5KB pre-sized captures — the
                            optimizer adds nothing and its cache once served
                            stale 404s for them. */}
                        <Image
                          src={`/personas/orb-${p.preset}.webp`}
                          alt=""
                          aria-hidden
                          width={256}
                          height={256}
                          unoptimized
                          className="absolute inset-0 size-full rounded-full"
                        />
                        {/* VoiceOrb's root is `relative` + this class — it must
                            carry its own box (NOT absolute; Tailwind's position
                            order lets the internal `relative` win, which left a
                            zero-sized canvas). It fills the 8rem slot in flow,
                            covering the poster beneath. */}
                        {isFront ? (
                          <VoiceOrb
                            state={isPlaying ? 'talking' : 'idle'}
                            preset={p.preset}
                            levelRef={isPlaying ? level : undefined}
                            glow={0}
                            grain={0}
                            className="size-32"
                          />
                        ) : null}
                      </span>
                      {/* Palm-green play control — half inside the orb, half out. */}
                      {isFront ? (
                        <span className="absolute bottom-0 start-1/2 grid size-10 -translate-x-1/2 translate-y-1/2 place-items-center rounded-full bg-primary text-primary-foreground shadow-card transition-brand group-hover:scale-105 rtl:translate-x-1/2">
                          {isPlaying ? (
                            <Pause className="size-4 fill-current" aria-hidden />
                          ) : (
                            <Play className="ms-0.5 size-4 fill-current" aria-hidden />
                          )}
                        </span>
                      ) : null}
                    </span>

                    {/* Each voice's signature mark over a sparkle divider —
                        fading with its row. */}
                    <div
                      className={cn(
                        'flex flex-col items-center transition-opacity duration-500',
                        row === 0 ? 'opacity-100' : row === 1 ? 'opacity-60' : 'opacity-40',
                      )}
                    >
                      <Glyph className="mt-6 size-4 text-primary/70" aria-hidden />
                      <span className="mt-3 flex items-center gap-2" aria-hidden>
                        <span className="h-px w-6 bg-border" />
                        <BrandSparkle
                          className={cn('size-2', isFront ? 'text-brand-gold/80' : 'text-border')}
                        />
                        <span className="h-px w-6 bg-border" />
                      </span>
                      <span className="mt-3 block font-display text-h4 text-foreground">
                        {items[i]?.name}
                      </span>
                      <span className="mt-2 block text-small text-muted-foreground">
                        {items[i]?.tagline}
                      </span>
                    </div>
                  </span>
                </div>
              );
            })}
          </div>

          {/* Back / next — flanking the deck at orb height. The wrapping span
              carries the absolute positioning: the global [data-slot='button']
              hover gesture pins buttons to position:relative. */}
          <span
            className="absolute top-[31%] z-20 -translate-y-1/2"
            style={{ insetInlineStart: 'calc(50% - 118px)' }}
          >
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => goto(active - 1)}
              aria-label={t('previous')}
              className="border-border bg-card text-muted-foreground hover:text-primary"
            >
              <ArrowLeft
                className="size-4 rtl:-scale-x-100"
                aria-hidden
                data-directional-icon-decorative
              />
            </Button>
          </span>
          <span
            className="absolute top-[31%] z-20 -translate-y-1/2"
            style={{ insetInlineEnd: 'calc(50% - 118px)' }}
          >
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => goto(active + 1)}
              aria-label={t('next')}
              className="border-border bg-card text-muted-foreground hover:text-primary"
            >
              <ArrowRight
                className="size-4 rtl:-scale-x-100"
                aria-hidden
                data-directional-icon-decorative
              />
            </Button>
          </span>
        </div>
        <div className="mt-8 flex justify-center">
          <SectionLink href="/products/create#voices">{t('learnMore')}</SectionLink>
        </div>
      </div>
      <audio ref={audioRef} onEnded={() => setPlaying(false)} preload="none" className="hidden" />
    </section>
  );
}
